import { useEffect, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Toaster } from '@/components/ui/sonner';
import {
  LayoutDashboard, Building2, PlusCircle, Briefcase, Users, LogOut, Menu, X,
  Loader2, FolderTree, Image as ImageIcon, FileText, Home, Store, UserCircle2,
  FileSignature, Zap, Receipt, IndianRupee, Calendar as CalendarIcon,
  MessageCircle, Send, BarChart3, Wallet, FolderOpen, ShieldCheck,
  Settings, History, MessagesSquare,
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_b771c267-6e6a-452f-b6b8-f219955187da/artifacts/a7yev6rp_Shree%20Bajrang%2055.png";

// Grouped sidebar. Items with `soon: true` render a "Coming soon" placeholder.
const sidebarGroups = [
  {
    label: 'OVERVIEW',
    items: [
      { label: 'ERP Dashboard', href: '/dashboard', icon: LayoutDashboard },
    ],
  },
  {
    label: 'RENTAL MANAGEMENT',
    items: [
      { label: 'Buildings', href: '/dashboard/erp/buildings', icon: Building2 },
      { label: 'Rooms & Shops', href: '/dashboard/erp/units', icon: Store },
      { label: 'Tenants', href: '/dashboard/erp/tenants', icon: UserCircle2 },
      { label: 'Agreements', href: '/dashboard/erp/agreements', icon: FileSignature },
      { label: 'Rent Collection', href: '/dashboard/erp/rent-collection', icon: IndianRupee, soon: true },
      { label: 'Rent Bills', href: '/dashboard/erp/rent-bills', icon: Receipt, soon: true },
      { label: 'Rent Receipts', href: '/dashboard/erp/rent-receipts', icon: Receipt, soon: true },
    ],
  },
  {
    label: 'EMI MANAGEMENT',
    items: [
      { label: 'EMI Customers', href: '/dashboard/erp/emi/customers', icon: UserCircle2 },
      { label: 'EMI Plans', href: '/dashboard/erp/emi/plans', icon: FileSignature },
      { label: 'EMI Collection', href: '/dashboard/erp/emi/collection', icon: IndianRupee, soon: true },
      { label: 'EMI Bills', href: '/dashboard/erp/emi/bills', icon: Receipt, soon: true },
      { label: 'EMI Receipts', href: '/dashboard/erp/emi/receipts', icon: Receipt, soon: true },
    ],
  },
  {
    label: 'OPERATIONS',
    items: [
      { label: 'Electricity', href: '/dashboard/erp/electricity', icon: Zap, soon: true },
      { label: 'Payments', href: '/dashboard/erp/payments', icon: Wallet, soon: true },
      { label: 'Calendar', href: '/dashboard/erp/calendar', icon: CalendarIcon, soon: true },
    ],
  },
  {
    label: 'COMMUNICATION',
    items: [
      { label: 'WhatsApp', href: '/dashboard/erp/whatsapp', icon: MessageCircle, soon: true },
      { label: 'SMS', href: '/dashboard/erp/sms', icon: Send, soon: true },
      { label: 'Documents', href: '/dashboard/erp/documents', icon: FolderOpen, soon: true },
    ],
  },
  {
    label: 'REPORTS',
    items: [
      { label: 'Reports', href: '/dashboard/erp/reports', icon: BarChart3, soon: true },
      { label: 'Finance', href: '/dashboard/erp/finance', icon: Wallet, soon: true },
    ],
  },
  {
    label: 'SYSTEM',
    items: [
      { label: 'Audit Logs', href: '/dashboard/erp/audit-logs', icon: History },
      { label: 'Users & Staff', href: '/dashboard/erp/users', icon: ShieldCheck, soon: true },
      { label: 'Settings', href: '/dashboard/erp/settings', icon: Settings, soon: true },
    ],
  },
  {
    label: 'WEBSITE CMS',
    items: [
      { label: 'Properties', href: '/dashboard/properties', icon: Home },
      { label: 'Categories', href: '/dashboard/categories', icon: FolderTree },
      { label: 'Hero & Site', href: '/dashboard/hero', icon: ImageIcon },
      { label: 'Blog', href: '/dashboard/articles', icon: FileText },
      { label: 'Jobs', href: '/dashboard/jobs', icon: Briefcase },
      { label: 'Leads', href: '/dashboard/leads', icon: MessagesSquare },
      { label: 'Add Property', href: '/dashboard/properties/new', icon: PlusCircle },
    ],
  },
];

export default function DashboardLayout() {
  const { isAuthenticated, loading, logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated) navigate('/admin/login');
  }, [isAuthenticated, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-orange" />
      </div>
    );
  }
  if (!isAuthenticated) return null;

  const handleLogout = () => { logout(); navigate('/admin/login'); };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="dashboard-layout">
      <Toaster position="top-right" richColors />

      <header className="fixed top-0 left-0 right-0 h-16 bg-white border-b z-50 flex items-center px-4 lg:px-8">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="lg:hidden p-2 rounded-md hover:bg-gray-100"
          data-testid="sidebar-toggle"
        >
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>

        <Link to="/dashboard" className="flex items-center space-x-3">
          <img src={LOGO_URL} alt="Logo" className="h-10" />
          <span className="font-heading font-semibold text-lg hidden sm:block">
            Shree Bajrang · ERP
          </span>
        </Link>

        <div className="ml-auto flex items-center space-x-4">
          <span className="text-sm text-gray-600 hidden sm:block">
            Welcome, {user?.username || 'Admin'}
          </span>
          <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>
      </header>

      <aside
        className={`fixed top-16 left-0 bottom-0 w-64 bg-white border-r transform transition-transform z-40 overflow-y-auto ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="sidebar"
      >
        <nav className="p-3 pb-24">
          {sidebarGroups.map((group) => (
            <div key={group.label} className="mb-4">
              <p className="px-3 text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5">
                {group.label}
              </p>
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const isActive =
                    location.pathname === item.href ||
                    (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={`sidebar-link flex items-center justify-between space-x-2 px-3 py-2 rounded-md text-sm transition-colors ${
                        isActive
                          ? 'bg-gradient-to-r from-brand-orange/10 to-transparent text-brand-orange font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                      data-testid={`sidebar-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    >
                      <span className="flex items-center gap-2.5">
                        <item.icon className="h-4 w-4 shrink-0" />
                        <span className="truncate">{item.label}</span>
                      </span>
                      {item.soon && (
                        <span className="text-[9px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full font-semibold">SOON</span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}

          <Link
            to="/"
            target="_blank"
            className="mx-3 mt-4 flex items-center justify-center space-x-2 px-4 py-2 text-xs text-gray-600 hover:text-brand-orange border rounded-lg"
          >
            <span>View Public Website ↗</span>
          </Link>
        </nav>
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <main className="lg:ml-64 pt-16 min-h-screen">
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
